/**
 * React Query hooks for the per-tenant QBO tax-code map admin UI (R2 —
 * SPEC-qbo-csv-harden-2026-06-20).
 *
 * The map (Keystone tax code -> QBO exact tax-code name) is stored in the
 * Cloud SQL tenant_settings table and read at export time by
 * services/qbo_csv_export.py. These hooks back the IntegrationsTab admin form
 * that lets an admin view/add/edit/remove entries instead of running raw SQL.
 *
 * Mutation-hook field discipline (known Keystone failure mode): the mutation
 * sends the ENTIRE map object as `{ tax_code_map }` — no per-key field-picking.
 * The whole map round-trips end-to-end, so add/edit/remove all persist and a
 * removed key is genuinely dropped.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_API_URL;
if (!API_BASE) {
  throw new Error('VITE_API_URL environment variable is required.');
}

async function getAuthToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function fetchWithAuth(endpoint: string, options?: RequestInit) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || response.statusText);
  }
  return response.json();
}

// ============================================================================
// Types
// ============================================================================

/** Flat map of Keystone tax code (string) -> QBO tax code name (string). */
export type QboTaxCodeMap = Record<string, string>;

export interface QboTaxCodeMapResponse {
  success: boolean;
  tax_code_map: QboTaxCodeMap;
}

const QUERY_KEY = ['settings', 'qbo-tax-code-map'] as const;

// ============================================================================
// Query Hook
// ============================================================================

export function useQboTaxCodeMap() {
  return useQuery<QboTaxCodeMap>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const data = (await fetchWithAuth(
        '/api/me/settings/qbo-tax-code-map',
      )) as QboTaxCodeMapResponse;
      // Carry the WHOLE map through — never reshape into a fixed key set.
      return data.tax_code_map ?? {};
    },
  });
}

// ============================================================================
// Mutation Hook
// ============================================================================

export function useUpdateQboTaxCodeMap() {
  const queryClient = useQueryClient();

  return useMutation({
    // The entire map is the payload. No explicit field-pick — every entry the
    // caller passes is sent as-is so the full map round-trips.
    mutationFn: (taxCodeMap: QboTaxCodeMap) =>
      fetchWithAuth('/api/me/settings/qbo-tax-code-map', {
        method: 'PUT',
        body: JSON.stringify({ tax_code_map: taxCodeMap }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('QBO tax-code map saved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save QBO tax-code map');
    },
  });
}
